/**
 * Reaksiya tezligi, muvozanat va katalizator faolligi.
 *
 * TENGLAMALAR
 *   k(T)   = k0 · exp(−Ea/(R·T)) · a                            Arrhenius, a — katalizator faolligi
 *   r_f    = k · Π_reagent C_i^{n_i}                              n_i = orders[i] yoki |ν_i|
 *   r      = r_f                                                 qaytmas
 *   r      = k · [Π_reagent C_i^{|ν_i|} − (1/Kc) Π_mahsulot C_j^{ν_j}]   qaytar (massalar ta'siri)
 *   Kc     = Kp · (p°/(R·T))^{Δν}   (gaz, p° = 1 bar; Kp(T) van't Hoff),  Kc(T) van't Hoff (suyuqlik)
 *   a(t)   = a0·exp(−kd t)  yoki  a0·max(0, 1 − kd t)
 *   Gaz konsentratsiyasi: C_i = n_i / q,  q = n_tot R T / P (ideal gaz)
 *   Suyuqlik: q = Σ n_i M_i / ρ_i(T)   (ideal aralashma, hajm additiv)
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR
 *   1. Qaytar reaksiyada orqa tezlik termodinamik izchillik uchun Kc orqali (orders e'tiborsiz).
 *   2. ΔH_r doimiy; Kp van't Hoff bo'yicha (Kirchhoff tuzatishsiz).
 *   3. Ideal gaz/eritma; diffuzion cheklovlar (samaradorlik omili) yo'q.
 *   4. Haqiqiy sanoat kinetikalari (Temkin–Pyzhev, Eklund) ko'pincha boshqa shaklda —
 *      kutubxonadagi `provisional: true` reaksiyalar ekspert tekshiruvini talab qiladi.
 *
 * MANBA: Fogler, "Elements of Chemical Reaction Engineering", 3–4, 8, 10-boblar;
 *        Smith–Van Ness–Abbott, 13-bob (Kp, Kc bog'liqligi).
 */
import { R_GAS, enthalpyChange, getSubstance, heatCapacity, liquidDensity } from '../properties';
import { kpVantHoff } from '../thermo';
import type { Phase } from '../types';
import type { ReactionDef } from './schema';

export const P_STD = 1e5; // Pa, 1 bar

/** Katalizator faolligi yoshga qarab (s); katalizator bo'lmasa 1 */
export function catalystActivity(r: ReactionDef, age = 0): number {
  const c = r.catalyst;
  if (!c) return 1;
  const a0 = c.activity;
  if (!c.deactivation || age <= 0) return a0;
  const { type, kd } = c.deactivation;
  return type === 'exponential' ? a0 * Math.exp(-kd * age) : a0 * Math.max(0, 1 - kd * age);
}

/** Oldinga tezlik konstantasi k(T)·a */
export function rateConstant(r: ReactionDef, T: number, activity = 1): number {
  return r.kinetics.k0 * Math.exp(-r.kinetics.Ea / (R_GAS * T)) * activity;
}

/** Reaksiya bo'yicha Σν (gaz mollari o'zgarishi) */
export function deltaNu(r: ReactionDef): number {
  return Object.values(r.stoich).reduce((a, b) => a + b, 0);
}

/** Muvozanat konstantasi konsentratsiya asosida, (mol/m³)^Δν; qaytmas bo'lsa null */
export function equilibriumKc(r: ReactionDef, T: number): number | null {
  const e = r.equilibrium;
  if (!e) return null;
  if (r.phase === 'gas') {
    const Kp = kpVantHoff(e.Kp0 as number, e.T0, r.dHr, T);
    return Kp * Math.pow(P_STD / (R_GAS * T), deltaNu(r));
  }
  return kpVantHoff(e.Kc0 as number, e.T0, r.dHr, T);
}

/** Tezlik, mol/(m³·s), konsentratsiyalar C (mol/m³) va T bo'yicha */
export function reactionRate(r: ReactionDef, C: Record<string, number>, T: number, activity = 1): number {
  const k = rateConstant(r, T, activity);
  let fwd = k;
  let rev = 0;
  const Kc = equilibriumKc(r, T);
  for (const [c, nu] of Object.entries(r.stoich)) {
    const conc = Math.max(0, C[c] ?? 0);
    if (nu < 0) {
      const n = Kc === null ? (r.kinetics.orders?.[c] ?? -nu) : -nu;
      fwd *= Math.pow(conc, n);
    } else if (Kc !== null) {
      rev = rev === 0 && nu > 0 ? 1 : rev;
      rev *= Math.pow(conc, nu);
    }
  }
  if (Kc === null) return fwd;
  return fwd - (k / Kc) * rev;
}

/** Hajmiy sarf, m³/s */
export function volumetricFlow(flow: Record<string, number>, T: number, P: number, phase: Phase): number {
  if (phase === 'gas') {
    const n = Object.values(flow).reduce((a, b) => a + Math.max(0, b), 0);
    return (n * R_GAS * T) / P;
  }
  let q = 0;
  for (const [id, n] of Object.entries(flow)) {
    if (n <= 0) continue;
    const s = getSubstance(id);
    q += (n * s.molarMass) / liquidDensity(s, T);
  }
  return q;
}

/** Konsentratsiyalar mol/m³ */
export function concentrations(flow: Record<string, number>, T: number, P: number, phase: Phase): Record<string, number> {
  const q = volumetricFlow(flow, T, P, phase);
  const C: Record<string, number> = {};
  for (const [id, n] of Object.entries(flow)) C[id] = q > 0 ? Math.max(0, n) / q : 0;
  return C;
}

/** Oqimning molyar issiqlik sig'imi yig'indisi Σ F_i cp_i, J/(s·K) */
export function flowHeatCapacity(flow: Record<string, number>, T: number, phase: Phase): number {
  let s = 0;
  for (const [id, n] of Object.entries(flow)) {
    if (n <= 0) continue;
    const sub = getSubstance(id);
    s += n * sub.molarMass * heatCapacity(sub, phase, T);
  }
  return s;
}

/** Komponentlarning molyar sezgir entalpiyasi h_i(T) = M_i ∫_{Tref}^{T} cp_i dT, J/mol */
export function speciesEnthalpy(comps: readonly string[], T: number, phase: Phase, Tref: number): Record<string, number> {
  const h: Record<string, number> = {};
  for (const c of comps) {
    const s = getSubstance(c);
    h[c] = s.molarMass * enthalpyChange(s, phase, Tref, T);
  }
  return h;
}

/**
 * Muvozanat konversiyasi (bitta qaytar reaksiya, T va P o'zgarmas): r(ξ) = 0 → ξ_eq (bisection).
 * Qaytmas reaksiya uchun 1 (to'liq konversiya) qaytadi.
 */
export function equilibriumConversion(r: ReactionDef, flow: Record<string, number>, T: number, P: number): number {
  const nuKey = r.stoich[r.key] as number;
  const F0 = flow[r.key] ?? 0;
  if (F0 <= 0) return 0;
  if (!r.equilibrium) return 1;
  // ξ_max: birinchi tugaydigan reagent bo'yicha
  let xiMax = Infinity;
  for (const [c, nu] of Object.entries(r.stoich)) if (nu < 0) xiMax = Math.min(xiMax, (flow[c] ?? 0) / -nu);
  const f = (xi: number) => {
    const F: Record<string, number> = { ...flow };
    for (const [c, nu] of Object.entries(r.stoich)) F[c] = (F[c] ?? 0) + nu * xi;
    return reactionRate(r, concentrations(F, T, P, r.phase), T);
  };
  let lo = 0;
  let hi = xiMax * (1 - 1e-9);
  if (f(lo) <= 0) return 0;
  if (f(hi) >= 0) return (xiMax * -nuKey) / F0;
  for (let i = 0; i < 100; i++) {
    const mid = 0.5 * (lo + hi);
    if (f(mid) > 0) lo = mid;
    else hi = mid;
    if (hi - lo < 1e-12 * xiMax) break;
  }
  return (0.5 * (lo + hi) * -nuKey) / F0;
}
